/**
 * Main authenticated app view chrome (map/calendar/filters/layouts).
 * Workspace prop bundles come from `workspaces/` (plan 3.1).
 */
import React, { useMemo } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import moment from 'moment';
import TopBar, { CALENDAR_VIEW_KEYS } from './TopBar';
import Sidebar from './Sidebar';
import StatStrip from './StatStrip';
import FilterDrawer from './FilterDrawer';
import LoginModal from './LoginModal';
import ConfirmDialog from './ConfirmDialog';
import { t } from './i18n';
import { getNextCalendarPanelMode, PANEL_COLLAPSED, PANEL_NORMAL, PANEL_EXPANDED } from './calendarPanel';
import { mapLoadPhaseLabel } from './mapDataFetch';
import { AppModals } from './ModalsProvider';
import {
  filterGlobalSearchItems,
  getTimelineHighlightsForDate,
  TimelineHighlightBadges,
  TimelineHighlightsSummary,
  MapLayerLegend,
  GlobalSearchPalette,
} from './appHelpers';
import AtlasVivoLayout from './layouts/AtlasVivoLayout';
import {
  buildAtlasContainer,
  buildDiaContainer,
  buildHistoriasContainer,
  buildDadosContainer,
  buildPulseRailContainer,
} from './workspaces';

const localizer = momentLocalizer(moment);

export default function AppMainView(p) {
  const {
    activeLegendItems, currentFilters, handleLegendLayerToggle, appTarget, loading, mapInitialized, statStripSummary,
    calendarPanelMode, setCalendarPanelMode, calendarDialogRef, calendarDragPos, handleCalendarDragStart,
    handleCalendarNavigate, calendarDate, calendarView, calendarEvents, handleCalendarView, handleCalendarDaySelect,
    handleCalendarSlotSelect, handleCalendarSelectEvent, eventPropGetter, dayPropGetter, timelineHighlights,
    handleTimelineHighlightSelect, handleShowOnlyThisDay, selectedDayHighlights, selectedDayEvents,
    handleFilterChange, atlasVivoActive, handleGoToImport, openImport, setIsAnniversaryOpen, setIsDiaryStatsOpen,
    setIsDashboardOpen, setIsGithubOpen, setIsMemoryBookOpen, openGlobalSearch, activeNavigationPresetKey,
    activeNavigationPreset, hasActivePresetResults, handleNavigationPresetSelect, handlePresetWidenPeriod,
    user, handleLogout, isDashboardOpen, isImportModalOpen, setIsImportModalOpen, setIsFiltersCollapsed, isFiltersCollapsed,
    isFilterDrawerOpen, setIsFilterDrawerOpen, preserveViewport, setPreserveViewport, clusterMarkers, setClusterMarkers,
    showDwellHeatmap, setShowDwellHeatmap, searchVenues, handleVenueSelect, searchCities, handleCitySelect,
    tagFilter, handleTagFilterApply, handleTagFilterClear, tagFilterVenueIds, routeSearchTerm, setRouteSearchTerm,
    visibleRouteItems, filteredRouteItems, selectedRouteId, handleRouteSelect, dataQualityReport, hiddenQualityIssueIds,
    handleToggleHiddenQualityIssue, handleApplyQualityCorrection, trips, selectedTripId, handleTripSelect,
    privacyConfig, setPrivacyConfig,
    isGlobalSearchOpen, globalSearchTerm, setGlobalSearchTerm, filteredGlobalSearchItems, outOfPeriodSearchItems,
    searchPeriodSnapshot, globalSearchItems,
    deferredGlobalSearchTerm, globalSearchActiveIndex, setGlobalSearchActiveIndex, closeGlobalSearch,
    handleGlobalSearchSelect, handleRestoreSearchPeriod,
    modalsState, safeFetchedData, fetchDataFromBackend, insights, insightsLoadedData, homeBase,
    handleDashboardCardAction, handlePrintPeriodSummary, handleShareTrip, handlePrintTrip, anniversaryItems,
    handleAnniversarySelect, handleAnniversaryDateChange, cityReplay, setCityReplay, replayIndex, replayPlaying,
    replaySpeed, setReplaySpeed, handleReplayPlay, handleReplayPause, handleReplayRestart, handleReplayExport,
    replayExportState, replayExportError,
    atlasSelection, setAtlasSelection, setSelectedPlaceForTags, selectCalendarDayOnly,
    workspaceView, setWorkspaceView, activeStoryKey, setActiveStoryKey, pendingManualForm, setPendingManualForm,
    pendingImportFocus, setPendingImportFocus,
    pulseRailModel, setRailHoverPosition, setRailFocusPosition, railWorkspaceRequest, setRailWorkspaceRequest,
    atlasVivoEventDays, datasetEmpty, atlasHeadline, fetchError, retryFetch,
    authLoading, handleLoginSuccess, mapLoadPhase, cancelMapFetch, pendingQualityIssue, confirmQualityCorrection,
    setPendingQualityIssue,
    offlineCachedAt, isOnline, canInstall, onInstallApp,
  } = p;

  const mapPanel = (
    <div className="map-pane">
      <div id="map" style={{ height: '100%', width: '100%' }}></div>
      <MapLayerLegend
        items={activeLegendItems}
        isolatedLayerKey={currentFilters.focusedKey}
        onToggle={handleLegendLayerToggle}
      />
      {appTarget !== 'mobile' && !loading && (
        <StatStrip
          distanceKm={statStripSummary.distanceKm}
          places={statStripSummary.places}
          streakDays={statStripSummary.streakDays}
          shows={statStripSummary.shows}
        />
      )}
      {loading && mapInitialized && (
        <div className="loading-overlay map-loading-overlay">
          <div className="spinner"></div>
        </div>
      )}
    </div>
  );

  const calendarComponents = useMemo(() => ({
    month: {
      dateHeader: ({ date, label }) => {
        const highlights = getTimelineHighlightsForDate(timelineHighlights, date);
        return (
          <div className="timeline-date-header">
            <span className="timeline-date-label">{label}</span>
            <TimelineHighlightBadges
              highlights={highlights}
              compact
              onSelectDate={handleTimelineHighlightSelect}
            />
          </div>
        );
      },
    },
  }), [timelineHighlights, handleTimelineHighlightSelect]);

  const calendarPanel = calendarPanelMode === PANEL_COLLAPSED ? (
    <button
      type="button"
      className="calendar-minimize-chip"
      onClick={() => setCalendarPanelMode((mode) => getNextCalendarPanelMode(mode, 'toggle'))}
    >
      📅 {moment(calendarDate).format('MMMM YYYY')}
      <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>▼</span>
    </button>
  ) : (
    <div
      ref={calendarDialogRef}
      className={`calendar-floating-bl${calendarPanelMode === PANEL_EXPANDED ? ' calendar-expanded' : ''}`}
      role={calendarPanelMode === PANEL_EXPANDED ? 'dialog' : undefined}
      aria-modal={calendarPanelMode === PANEL_EXPANDED ? 'true' : undefined}
      aria-label={calendarPanelMode === PANEL_EXPANDED ? t('web.calendar.expanded') : undefined}
      tabIndex={calendarPanelMode === PANEL_EXPANDED ? -1 : undefined}
      style={calendarPanelMode === PANEL_EXPANDED && calendarDragPos ? {
        top: calendarDragPos.top, left: calendarDragPos.left, right: 'auto', bottom: 'auto', transform: 'none',
      } : undefined}
    >
      <div className="floating-panel calendar-pane">
        <div
          className="calendar-side-header"
          onPointerDown={handleCalendarDragStart}
          style={calendarPanelMode === PANEL_EXPANDED ? { cursor: 'grab' } : undefined}
        >
          <button type="button" onClick={() => handleCalendarNavigate(moment(calendarDate).subtract(1, 'month').toDate(), 'month')} aria-label={t('web.calendar.prevMonth')}>‹</button>
          <h2>{moment(calendarDate).format('MMMM YYYY')}</h2>
          <button type="button" onClick={() => handleCalendarNavigate(moment(calendarDate).add(1, 'month').toDate(), 'month')} aria-label={t('web.calendar.nextMonth')}>›</button>
          <button
            type="button"
            className="calendar-expand-btn"
            onClick={() => setCalendarPanelMode((mode) => getNextCalendarPanelMode(mode, 'expand'))}
            aria-label={calendarPanelMode === PANEL_NORMAL ? t('web.calendar.expand') : t('web.calendar.restore')}
            title={calendarPanelMode === PANEL_NORMAL ? t('web.calendar.expand') : t('web.calendar.restore')}
          >
            ⛶
          </button>
          <button
            type="button"
            className="calendar-minimize-btn"
            onClick={() => setCalendarPanelMode((mode) => {
              if (mode === PANEL_EXPANDED) return getNextCalendarPanelMode(mode, 'collapse');
              return getNextCalendarPanelMode(mode, 'toggle');
            })}
            aria-label={t('web.calendar.minimize')}
            title={t('web.calendar.minimize')}
          >
            ▲
          </button>
        </div>
        <Calendar
          localizer={localizer}
          events={calendarEvents}
          date={calendarDate}
          view={calendarView}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 320 }}
          onNavigate={handleCalendarNavigate}
          onView={handleCalendarView}
          onDrillDown={handleCalendarDaySelect}
          selectable="ignoreEvents"
          onSelectSlot={handleCalendarSlotSelect}
          onSelectEvent={handleCalendarSelectEvent}
          eventPropGetter={eventPropGetter}
          dayPropGetter={dayPropGetter}
          components={calendarComponents}
          views={CALENDAR_VIEW_KEYS}
          messages={{
            next: t('web.calendar.nextShort'),
            previous: t('web.calendar.previous'),
            today: t('web.calendar.today'),
            month: t('web.calendar.month'),
            week: t('web.calendar.week'),
            day: t('web.calendar.day'),
            agenda: t('web.calendar.agenda'),
            date: t('web.calendar.date'),
            time: t('web.calendar.time'),
            event: t('web.calendar.event'),
            noEventsInRange: t('web.calendar.noEventsPeriod'),
          }}
        />
        <div className="calendar-day-events">
          <div className="calendar-day-events-header">
            <h3>{t('web.calendar.dayEvents', { date: moment(calendarDate).format('DD/MM/YYYY') })}</h3>
            <button
              type="button"
              className={`calendar-day-focus-btn${
                currentFilters.startDate === moment(calendarDate).format('YYYY-MM-DD')
                && currentFilters.endDate === moment(calendarDate).format('YYYY-MM-DD')
                  ? ' is-active' : ''
              }`}
              onClick={handleShowOnlyThisDay}
            >
              {t('web.calendar.showOnlyDay')}
            </button>
          </div>
          <TimelineHighlightsSummary
            date={calendarDate}
            highlights={selectedDayHighlights}
          />
          {selectedDayEvents.length === 0 ? (
            <p className="calendar-day-empty">{t('web.calendar.noEvents')}</p>
          ) : (
            <div className="calendar-day-list">
              {selectedDayEvents.map((event, index) => (
                <button
                  key={`${event.title}-${index}`}
                  type="button"
                  className={`calendar-day-event${event.resource?.muted ? ' calendar-day-event-muted' : ''}`}
                  onClick={() => handleCalendarSelectEvent(event)}
                >
                  <span className="calendar-day-event-dot" style={{ backgroundColor: event.resource?.color || '#6366f1' }} />
                  <span className="calendar-day-event-main">
                    <strong>
                      {event.resource?.type === 'diary'
                        ? `${t('label.diary')}${event.resource?.data?.note_title ? ` ${event.resource.data.note_title}` : ''}`
                        : event.title}
                    </strong>
                    <small>
                      {event.resource?.type === 'diary'
                        ? (event.resource?.data?.mood || '')
                        : (event.resource?.data?.venue || event.resource?.data?.venue_name || event.resource?.data?.artist_name || '')}
                    </small>
                  </span>
                  <span className="calendar-day-event-time">
                    {event.allDay ? '' : moment(event.start).format('HH:mm')}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const topBar = (
    <TopBar
      currentFilters={currentFilters}
      onFilterChange={handleFilterChange}
      onOpenDrawer={() => setIsFilterDrawerOpen(true)}
      onOpenImport={atlasVivoActive ? handleGoToImport : () => openImport()}
      onOpenAnniversary={() => setIsAnniversaryOpen(true)}
      onOpenStats={() => setIsDiaryStatsOpen(true)}
      onOpenDashboard={() => setIsDashboardOpen(true)}
      onOpenGithub={() => setIsGithubOpen(true)}
      onOpenMemoryBook={() => setIsMemoryBookOpen(true)}
      onOpenSearch={openGlobalSearch}
      activeNavigationPresetKey={activeNavigationPresetKey}
      activeNavigationPreset={activeNavigationPreset}
      hasActivePresetResults={hasActivePresetResults}
      onNavigationPresetSelect={handleNavigationPresetSelect}
      onPresetWidenPeriod={handlePresetWidenPeriod}
      calendarDate={calendarDate}
      calendarView={calendarView}
      onCalendarNavigate={handleCalendarNavigate}
      onCalendarView={handleCalendarView}
      user={user}
      onLogout={handleLogout}
    />
  );

  const activeNav = isDashboardOpen
    ? 'insights'
    : isImportModalOpen
      ? 'import'
      : 'map';

  const handleSidebarNavigate = (key) => {
    setIsDashboardOpen(key === 'insights');
    setIsImportModalOpen(key === 'import');
    if (key === 'filters') setIsFiltersCollapsed(false);
    if (key === 'map') setIsFiltersCollapsed(true);
  };

  const sidebar = appTarget !== 'mobile' ? (
    <Sidebar activeNav={activeNav} onNavigate={handleSidebarNavigate} />
  ) : null;

  const filtersCollapsed = appTarget !== 'mobile' && isFiltersCollapsed;

  const filterDrawer = filtersCollapsed ? (
    <button
      type="button"
      className="filters-collapse-chip"
      onClick={() => setIsFiltersCollapsed(false)}
      aria-label={t('web.filter.expand')}
    >
      <span aria-hidden="true">▸</span> Filtros
      {currentFilters.selectedTypes.length > 0 && (
        <span className="badge">{currentFilters.selectedTypes.length}</span>
      )}
    </button>
  ) : (
    <FilterDrawer
      isOpen={appTarget === 'mobile' ? isFilterDrawerOpen : true}
      isInline={appTarget !== 'mobile'}
      onClose={() => setIsFilterDrawerOpen(false)}
      onCollapse={appTarget !== 'mobile' ? () => setIsFiltersCollapsed(true) : undefined}
      currentFilters={currentFilters}
      onApplyFilters={handleFilterChange}
      preserveViewport={preserveViewport}
      onPreserveViewportChange={setPreserveViewport}
      clusterMarkers={clusterMarkers}
      onClusterMarkersChange={setClusterMarkers}
      showDwellHeatmap={showDwellHeatmap}
      onDwellHeatmapChange={setShowDwellHeatmap}
      onVenueSearch={searchVenues}
      onVenueSelect={handleVenueSelect}
      onCitySearch={searchCities}
      onCitySelect={handleCitySelect}
      tagFilter={tagFilter}
      onTagFilterApply={handleTagFilterApply}
      onTagFilterClear={handleTagFilterClear}
      tagFilterActive={Boolean(tagFilterVenueIds)}
      tagFilterCount={tagFilterVenueIds ? tagFilterVenueIds.size : 0}
      routeSearchTerm={routeSearchTerm}
      onRouteSearchTermChange={setRouteSearchTerm}
      routeItems={visibleRouteItems}
      filteredRouteItems={filteredRouteItems}
      selectedRouteId={selectedRouteId}
      onRouteSelect={handleRouteSelect}
      dataQualityReport={dataQualityReport}
      hiddenQualityIssueIds={hiddenQualityIssueIds}
      onToggleHiddenQualityIssue={handleToggleHiddenQualityIssue}
      onApplyQualityCorrection={handleApplyQualityCorrection}
      onOpenImport={atlasVivoActive ? handleGoToImport : () => openImport()}
      trips={trips.trips}
      selectedTripId={selectedTripId}
      onTripSelect={handleTripSelect}
      privacyConfig={privacyConfig}
      onPrivacyConfigChange={setPrivacyConfig}
    />
  );

  const globalLoadingOverlay = loading && !mapInitialized ? (
    <div
      className="loading-overlay global-loading-overlay"
      aria-busy="true"
      aria-live="assertive"
      aria-label={t('web.loading.yourData')}
    >
      <div className="skeleton-layout">
        <div className="skeleton-strip">
          <div className="skeleton-box sk-stat"></div>
          <div className="skeleton-box sk-stat"></div>
          <div className="skeleton-box sk-stat"></div>
          <div className="skeleton-box sk-stat"></div>
        </div>
        <div className="skeleton-main">
          <div className="skeleton-box sk-map"></div>
          <div className="skeleton-panel">
            <div className="skeleton-box sk-line"></div>
            <div className="skeleton-box sk-line short"></div>
            <div className="skeleton-box sk-line"></div>
            <div className="skeleton-box sk-line short"></div>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  const globalSearchPalette = (
    <GlobalSearchPalette
      isOpen={isGlobalSearchOpen}
      searchTerm={globalSearchTerm}
      onSearchTermChange={setGlobalSearchTerm}
      items={filteredGlobalSearchItems}
      outOfPeriodItems={outOfPeriodSearchItems}
      previousPeriod={searchPeriodSnapshot}
      onRestorePreviousPeriod={handleRestoreSearchPeriod}
      totalCount={filterGlobalSearchItems(globalSearchItems, deferredGlobalSearchTerm).length}
      activeIndex={globalSearchActiveIndex}
      onActiveIndexChange={setGlobalSearchActiveIndex}
      onClose={closeGlobalSearch}
      onSelect={handleGlobalSearchSelect}
    />
  );

  const modals = (
    <AppModals
      modals={modalsState}
      user={user}
      safeFetchedData={safeFetchedData}
      currentFilters={currentFilters}
      fetchDataFromBackend={fetchDataFromBackend}
      handleLogout={handleLogout}
      insights={insights}
      insightsLoadedData={insightsLoadedData}
      homeBase={homeBase}
      handleDashboardCardAction={handleDashboardCardAction}
      handlePrintPeriodSummary={handlePrintPeriodSummary}
      handleShareTrip={handleShareTrip}
      handlePrintTrip={handlePrintTrip}
      calendarDate={calendarDate}
      anniversaryItems={anniversaryItems}
      handleAnniversarySelect={handleAnniversarySelect}
      handleAnniversaryDateChange={handleAnniversaryDateChange}
      cityReplay={cityReplay}
      setCityReplay={setCityReplay}
      replayIndex={replayIndex}
      replayPlaying={replayPlaying}
      replaySpeed={replaySpeed}
      setReplaySpeed={setReplaySpeed}
      handleReplayPlay={handleReplayPlay}
      handleReplayPause={handleReplayPause}
      handleReplayRestart={handleReplayRestart}
      handleReplayExport={handleReplayExport}
      replayExportState={replayExportState}
      replayExportError={replayExportError}
    />
  );

  // Bundle de lentes / workspaces (plan 3.1) — builders em `workspaces/`.
  const { lensProps, atlasContext } = buildAtlasContainer({
    activeNavigationPresetKey,
    handleNavigationPresetSelect,
    atlasSelection,
    setAtlasSelection,
    setSelectedPlaceForTags,
  });

  const diaProps = buildDiaContainer({
    calendarDate,
    selectedDayEvents,
    atlasVivoEventDays,
    selectCalendarDayOnly,
    handleCalendarSelectEvent,
  });

  const historiasProps = buildHistoriasContainer({
    workspaceView,
    activeStoryKey,
    setActiveStoryKey,
    insights,
    insightsLoadedData,
    homeBase,
    currentFilters,
    handleDashboardCardAction,
    handlePrintPeriodSummary,
    safeFetchedData,
    user,
    calendarDate,
    anniversaryItems,
    handleAnniversarySelect,
    handleAnniversaryDateChange,
    searchCities,
    handleCitySelect,
    cityReplay,
    setWorkspaceView,
  });

  const dadosProps = buildDadosContainer({
    workspaceView,
    activeStoryKey,
    setActiveStoryKey,
    user,
    handleLogout,
    fetchDataFromBackend,
    currentFilters,
    pendingManualForm,
    setPendingManualForm,
    pendingImportFocus,
    setPendingImportFocus,
  });

  const pulseRail = buildPulseRailContainer({
    atlasVivoActive,
    currentFilters,
    pulseRailModel,
    setRailHoverPosition,
    setRailFocusPosition,
    selectCalendarDayOnly,
    setRailWorkspaceRequest,
    railWorkspaceRequest,
  });

  // Barra de escopo nativa do Atlas (fase 8). Reusa os mesmos handlers de
  // filtro/busca/sessão que o TopBar antigo usava — só troca a apresentação.
  const isOffline = Boolean(!isOnline || offlineCachedAt);
  const scopeBar = {
    currentFilters,
    onFilterChange: handleFilterChange,
    onOpenSearch: openGlobalSearch,
    user,
    onLogout: handleLogout,
    offline: isOffline,
    offlineCachedAt,
    canInstall,
    onInstallApp,
    periodEmpty: Boolean(
      !datasetEmpty
      && pulseRailModel
      && pulseRailModel.valid
      && pulseRailModel.maxBucketCount === 0
    ),
    loadedData: safeFetchedData,
  };

  const layoutProps = {
    mapPanel,
    calendarPanel,
    topBar,
    filterDrawer,
    filtersCollapsed,
    globalSearchPalette,
    globalLoadingOverlay,
    modals,
    sidebar,
    view: workspaceView,
    onViewChange: setWorkspaceView,
    lensProps,
    atlasContext,
    pulseRail,
    diaProps,
    historiasProps,
    dadosProps,
    scopeBar,
    headline: datasetEmpty ? null : atlasHeadline,
    datasetEmpty,
    onGoToImport: handleGoToImport,
    loading,
    error: fetchError,
    onRetry: retryFetch,
    offline: isOffline,
    offlineCachedAt,
  };

  if (authLoading) {
    return (
      <div
        className="atlas-vivo-scope av-auth-loading"
        role="status"
        aria-live="polite"
        aria-busy="true"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw',
          background: '#111315',
          color: '#9da3a3',
          fontSize: '15px',
          fontFamily: '"IBM Plex Sans", sans-serif',
          letterSpacing: '0.04em',
        }}
      >
        {t('web.boot.loadingJourney')}
      </div>
    );
  }

  if (!user) {
    return <LoginModal onLoginSuccess={handleLoginSuccess} />;
  }

  const errorBanner = fetchError ? (
    <div className="fetch-error-banner" role="alert">
      <span className="fetch-error-banner__msg">{fetchError}</span>
      <button
        type="button"
        className="fetch-error-banner__retry"
        onClick={retryFetch}
        disabled={loading}
      >
        {loading ? t('action.trying') : t('action.retry')}
      </button>
    </div>
  ) : null;

  const mapLoadBanner = mapLoadPhase ? (
    <div className="map-load-banner" role="status" aria-live="polite">
      <span className="map-load-banner__msg">{mapLoadPhaseLabel(mapLoadPhase)}</span>
      <button
        type="button"
        className="map-load-banner__cancel"
        onClick={cancelMapFetch}
      >
        {t('action.cancel')}
      </button>
    </div>
  ) : null;

  // Atlas Vivo is the only AppMainView shell (Épico 29 / nedi-telas 1.5).
  // Desktop/Mobile/WebLayout remain under layouts/ for unit harness only —
  // they are not imported on the production path (web / desktop / mobile targets).
  const layoutEl = <AtlasVivoLayout {...layoutProps} target={appTarget} />;

  return (
    <>
      {errorBanner}
      {mapLoadBanner}
      {layoutEl}
      <ConfirmDialog
        open={Boolean(pendingQualityIssue)}
        title={t('web.quality.fix')}
        message={
          pendingQualityIssue
            ? t('web.quality.message', { title: pendingQualityIssue.title })
            : ''
        }
        confirmLabel={t('action.removeRoute')}
        cancelLabel={t('action.cancel')}
        destructive
        onConfirm={confirmQualityCorrection}
        onCancel={() => setPendingQualityIssue(null)}
      />
    </>
  );

}
